import { useRef, useCallback, useEffect } from 'react';
import { isNumber } from 'lodash-es';

/**
 * Custom hook to manage cursor position in an input field
 * @param {Object} inputRef - React ref to the input element
 * @param {string} inputValue - Current input value
 * @param {boolean} isActive - Whether cursor tracking is active (e.g., during speech recognition)
 * @returns {Object} Cursor position management functions
 */
export function useInputCursorPosition(inputRef, inputValue, isActive) {
  const cursorPositionRef = useRef(0);
  const shouldUpdateCursorRef = useRef(false);
  const previousTranscriptLengthRef = useRef(0);

  // Update cursor position in the DOM after input value changes
  useEffect(() => {
    if (shouldUpdateCursorRef.current && inputRef.current && isActive) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      shouldUpdateCursorRef.current = false;
    }
  }, [inputValue, isActive, inputRef]);

  /**
   * Set the cursor position and schedule DOM update
   */
  const setCursorPosition = useCallback(position => {
    cursorPositionRef.current = position;
    shouldUpdateCursorRef.current = true;
  }, []);

  /**
   * Update cursor position from the input element's current selection
   */
  const updateCursorFromInput = useCallback(() => {
    if (inputRef.current && isNumber(inputRef.current.selectionStart)) {
      cursorPositionRef.current = inputRef.current.selectionStart;
    }
  }, [inputRef]);

  /**
   * Insert text at current cursor position and update cursor
   * @param {string} currentValue - Current input value
   * @param {string} textToInsert - Text to insert at cursor position
   * @returns {string} - New value with text inserted
   */
  const insertTextAtCursor = useCallback(
    (currentValue, textToInsert) => {
      const cursorPos = cursorPositionRef.current;
      const beforeCursor = currentValue.slice(0, cursorPos);
      const afterCursor = currentValue.slice(cursorPos);
      const newValue = beforeCursor + textToInsert + afterCursor;

      setCursorPosition(cursorPos + textToInsert.length);

      return newValue;
    },
    [setCursorPosition]
  );

  /**
   * Insert transcript at cursor position, handling incremental updates
   * @param {string} currentValue - Current input value
   * @param {string} fullTranscript - Complete transcript text
   * @returns {string|null} - New value if text was inserted, null if no change
   */
  const insertTranscriptAtCursor = useCallback(
    (currentValue, fullTranscript) => {
      // Calculate how much new text was added
      const newTextLength = fullTranscript.length - previousTranscriptLengthRef.current;

      if (newTextLength > 0) {
        // Get only the new text that was added
        const newText = fullTranscript.slice(previousTranscriptLengthRef.current);

        // Update the tracking length
        previousTranscriptLengthRef.current = fullTranscript.length;

        // Insert the new text at cursor position
        return insertTextAtCursor(currentValue, newText);
      }

      return null;
    },
    [insertTextAtCursor]
  );

  /**
   * Reset transcript tracking (call when user starts typing or stops listening)
   */
  const resetTranscriptTracking = useCallback(() => {
    previousTranscriptLengthRef.current = 0;
  }, []);

  return {
    insertTranscriptAtCursor,
    updateCursorFromInput,
    resetTranscriptTracking,
  };
}
